import { Text } from '@sitecore-jss/sitecore-jss-react';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { errorMessages } from '../../../utils/errors/constants';
import useErrorState from '../../../utils/errors/useErrorState';
import { resolve } from '../../../utils/object/pathResolver';

const DateQuestion = ({
  rendering,
  fields,
  displayErrors,
  defaultValue,
  onValidEntry,
  componentName,
  onError,
}) => {
  const dispatch = useDispatch();
  const [error, setError] = useErrorState(rendering.uid, fields.name?.value, fields, onError);
  const errorClass = error && displayErrors ? ' vds-form__input--invalid' : '';
  const [inputValue, setInputValue] = useState({
    day: '',
    month: '',
    year: '',
  });

  const parsedDate = value => new Date(Date.UTC(value.year, value.month - 1, value.day));

  const validate = value => {
    if (!value.day || !value.month || !value.year) {
      setError(errorMessages.EMPTY);
      return false;
    }
    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    // Adjust for leap years
    if (value.year % 400 === 0 || (value.year % 100 !== 0 && value.year % 4 === 0)) {
      monthLength[1] = 29;
    }
    if (
      value.year <= 999 ||
      value.month <= 0 ||
      value.month > 12 ||
      value.day <= 0 ||
      value.day > monthLength[value.month - 1]
    ) {
      setError(errorMessages.INVALID);
      return false;
    }
    if (fields.minAge) {
      let refDate = new Date();
      refDate.setDate(refDate.getDate() + fields.startDateDelay.value);
      const maxDoB = refDate.setFullYear(refDate.getFullYear() - fields.minAge.value);
      if (parsedDate(value).getTime() > maxDoB) {
        setError(errorMessages.TOO_YOUNG);
        return false;
      }
    }
    if (fields.maxAge) {
      let refDate = new Date();
      refDate.setDate(refDate.getDate() + fields.startDateDelay.value);
      const minDoB = refDate.setFullYear(refDate.getFullYear() - fields.maxAge.value);
      if (parsedDate(value).getTime() < minDoB) {
        setError(errorMessages.TOO_OLD);
        return false;
      }
    }
    setError(null);
    return true;
  };

  const dispatchReduxValue = isoValue => {
    if (!fields.reduxDispatchType?.value) {
      return;
    }
    const index = fields.reduxDispatchType?.payload?.index;
    const payload = index >= 0 ? { value: isoValue, index } : isoValue;
    dispatch({ type: fields.reduxDispatchType?.value, payload });
  };

  const advanceFocus = (datePart, value, maxLength) => {
    if (value.length !== maxLength) {
      return;
    }
    const baseId = componentName || rendering.uid;
    if (datePart === 'day') {
      document.getElementById(`${baseId}_month`)?.focus();
    } else if (datePart === 'month') {
      document.getElementById(`${baseId}_year`)?.focus();
    }
  };

  const handleUpdate = e => {
    const { name, maxLength } = e.target;
    const value = e.target.value.replace(/\D/g, '');
    if (value && value !== '0' && !Number.parseInt(value, 10)) {
      return;
    }
    const datePart = name.split('_').pop();
    const dateValue = { ...inputValue, [datePart]: value };
    setInputValue(dateValue);

    if (!dateValue.day && !dateValue.month && !dateValue.year) {
      dispatchReduxValue('');
      return;
    }

    advanceFocus(datePart, value, maxLength);

    if (validate(dateValue)) {
      const parsed = parsedDate(dateValue);
      if (onValidEntry) {
        onValidEntry(parsed);
      }
      dispatchReduxValue(parsed.toISOString());
    }

    e.target.value = value;
  };

  const handleBlur = e => {
    const { name } = e.target;
    let value = e.target.value.replace(/\D/g, '');
    if (value && value !== '0' && !Number.parseInt(value, 10)) {
      return;
    }
    const dtPart = name.split('_').pop();
    const dtValue = { ...inputValue, [dtPart]: formatDayMonth(value) };
    setInputValue(dtValue);
  };

  const formatDayMonth = val => {
    if (val) {
      return val.toString().padStart(2, '0');
    } else {
      return val;
    }
  };

  const handleKeyDown = e => {
    const backspaceKeyCode = 8;
    const { keyCode } = e;
    const { name, value } = e.target;
    const datePart = name.split('_').pop();
    if (!value && keyCode === backspaceKeyCode) {
      if (datePart === 'year') {
        document.getElementById(`${componentName || rendering.uid}_month`).focus();
      } else if (datePart === 'month') {
        document.getElementById(`${componentName || rendering.uid}_day`).focus();
      }
    }
  };

  useEffect(() => {
    if (
      fields.required?.value &&
      (!inputValue.day || !inputValue.month || !inputValue.year) &&
      !defaultValue
    ) {
      setError(errorMessages.EMPTY);
    } else {
      const defaultDate = new Date(defaultValue);
      if (!Number.isNaN(defaultDate.getTime())) {
        const prefilled = {
          day: formatDayMonth(defaultDate.getDate()),
          month: formatDayMonth(defaultDate.getMonth() + 1),
          year: defaultDate.getFullYear(),
        };
        setInputValue(prefilled);
        validate({
          day: Number(prefilled.day),
          month: Number(prefilled.month),
          year: Number(prefilled.year),
        });
      }
    }
    return () => {
      setError(null);
    };
  }, []);

  return (
    <div className="vds-form__group">
      <fieldset className="vds-form__fieldset">
        <Text tag="legend" className="vds-form__label" field={fields.title} />
        <div className="vds-form__date" name={fields.name?.value}>
          <div className="vds-form__date-item">
            <Text
              tag="label"
              className="vds-form__label"
              htmlFor={`${componentName || rendering.uid}_day`}
              field={fields.dayLabel}
            />
            <input
              id={`${componentName || rendering.uid}_day`}
              className={`vds-form__input vds-form__input--date${errorClass}`}
              name={`${componentName || rendering.uid}_day`}
              data-testid={`${fields.testid?.value}-day`}
              type="numeric"
              placeholder="DD"
              inputMode="decimal"
              pattern="[0-9]"
              maxLength="2"
              value={inputValue.day}
              onChange={handleUpdate}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              required={fields.required?.value}
            />
          </div>
          <div className="vds-form__date-item">
            <Text
              tag="label"
              className="vds-form__label"
              htmlFor={`${componentName || rendering.uid}_month`}
              field={fields.monthLabel}
            />
            <input
              id={`${componentName || rendering.uid}_month`}
              className={`vds-form__input vds-form__input--date${errorClass}`}
              name={`${componentName || rendering.uid}_month`}
              data-testid={`${fields.testid?.value}-month`}
              type="numeric"
              placeholder="MM"
              inputMode="decimal"
              pattern="[0-9]"
              maxLength="2"
              value={inputValue.month}
              onChange={handleUpdate}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              required={fields.required?.value}
            />
          </div>
          <div className="vds-form__date-item">
            <Text
              tag="label"
              className="vds-form__label"
              htmlFor={`${componentName || rendering.uid}_year`}
              field={fields.yearLabel}
            />
            <input
              id={`${componentName || rendering.uid}_year`}
              className={`vds-form__input vds-form__input--date${errorClass}`}
              name={`${componentName || rendering.uid}_year`}
              data-testid={`${fields.testid?.value}-year`}
              type="numeric"
              placeholder="YYYY"
              inputMode="decimal"
              pattern="[1-2][0-9]{3}"
              maxLength="4"
              value={inputValue.year}
              onChange={handleUpdate}
              onKeyDown={handleKeyDown}
              required={fields.required?.value}
            />
          </div>
        </div>
      </fieldset>
      {displayErrors && fields.inlineError?.value && error && (
        <div className="vds-form__group">
          <div className="vds-form__validation-message vds-form__validation-message--invalid">
            <span
              className="vds-form__validation-text"
              data-errormessageorigin={error.name}
              data-errormessagetype={error.type}
              data-testid="error-message"
            >
              {error.message}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const reduxValuePath = props.fields.reduxValuePath?.value;
  let defaultValue = props.defaultValue;
  if (reduxValuePath) {
    defaultValue = resolve(reduxValuePath, state);
  }
  return {
    ...props,
    defaultValue,
    displayErrors: props.displayErrors ?? state.appState.displayErrors,
  };
};

DateQuestion.propTypes = {
  rendering: PropTypes.shape({
    uid: PropTypes.string,
  }),
  fields: PropTypes.shape({
    name: PropTypes.shape({ value: PropTypes.string }),
    title: PropTypes.shape({ value: PropTypes.string }),
    testid: PropTypes.shape({ value: PropTypes.string }),
    dayLabel: PropTypes.shape({ value: PropTypes.string }),
    monthLabel: PropTypes.shape({ value: PropTypes.string }),
    yearLabel: PropTypes.shape({ value: PropTypes.string }),
    required: PropTypes.shape({ value: PropTypes.bool }),
    inlineError: PropTypes.shape({ value: PropTypes.bool }),
    minAge: PropTypes.shape({ value: PropTypes.number }),
    maxAge: PropTypes.shape({ value: PropTypes.number }),
    startDateDelay: PropTypes.shape({ value: PropTypes.number }),
    reduxDispatchType: PropTypes.shape({
      value: PropTypes.string,
      payload: PropTypes.shape({ index: PropTypes.number }),
    }),
    reduxValuePath: PropTypes.shape({ value: PropTypes.string }),
  }),
  displayErrors: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onValidEntry: PropTypes.func,
  componentName: PropTypes.string,
  onError: PropTypes.func,
};

export default connect(mapStateToProps)(DateQuestion);
